import {
  Children,
  type DependencyList,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/utils';

/** Items fetched for homepage list widgets. Cards show a few rows and scroll the rest. */
export const HOME_WIDGET_FETCH_LIMIT = 10;

/** Shared max height for the four homepage list widgets (~3 rows + a peek of the next). */
export const homeWidgetScrollListClassName =
  'max-h-52 min-h-0 overflow-y-auto overscroll-contain subtle-scrollbar';

export function useHomeWidgetScrollHint(
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList = [],
) {
  const [showFade, setShowFade] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const canScroll = el.scrollHeight > el.clientHeight + 2;
      const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
      setShowFade(canScroll && notAtBottom);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    if (observer) {
      for (const child of el.children) {
        observer.observe(child);
      }
    }

    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies content deps
  }, [ref, ...deps]);

  return showFade;
}

export function HomeWidgetScrollBottomFade({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t from-white via-white/70 to-transparent"
    />
  );
}

export function WidgetScrollList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const showFade = useHomeWidgetScrollHint(listRef, [Children.count(children)]);

  return (
    <div className="relative min-h-0">
      <ul
        ref={listRef}
        className={cn(
          homeWidgetScrollListClassName,
          'divide-y divide-slate-100/80',
          className,
        )}
      >
        {children}
      </ul>
      <HomeWidgetScrollBottomFade visible={showFade} />
    </div>
  );
}
